import React, { useEffect, useState } from "react";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../Lib/Firebase";
import AdminLogin from "./AdminLogin";
import { bootstrapAdminAccess } from "../services/adminAccess";
import AdminMfaSetup from "./security/AdminMfaSetup";

/**
 * Wrap /admin with this:
 * <AdminRoute><AdminPanel/></AdminRoute>
 * Handles 4 states:
 * - Not logged in -> show Google login
 * - Approved without MFA -> show only security setup
 * - Approved with a current MFA session -> render AdminPanel
 * - Not approved -> redirect to the student dashboard
 */
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [checking, setChecking] = useState(true);
    const [user, setUser] = useState<User | null>(null);
    const [access, setAccess] = useState<"signed-out" | "setup" | "verified">("signed-out");
    const navigate = useNavigate();

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (u) => {
            setUser(u);
            if (!u || !u.email) {
                setAccess("signed-out");
                setChecking(false);
                return;
            }
            try {
                const state = await bootstrapAdminAccess();
                if (!state.approved) {
                    navigate("/dashboard", { replace: true });
                } else if (!state.mfaEnrolled) {
                    setAccess("setup");
                } else if (!state.mfaVerified) {
                    await signOut(auth);
                    setAccess("signed-out");
                } else {
                    setAccess("verified");
                }
            } catch {
                await signOut(auth);
                setAccess("signed-out");
            } finally {
                setChecking(false);
            }
        });
        return () => unsub();
    }, [navigate]);

    if (checking) {
        return (
            <div className="min-h-screen grid place-items-center bg-gradient-to-br from-[#0b1120] via-[#111827] to-[#0f172a] text-slate-100">
                <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl px-6 py-4">Checking access…</div>
            </div>
        );
    }

    if (!user) {
        // Not logged in -> Google login
        return <AdminLogin />;
    }

    if (access === "setup") return <AdminMfaSetup user={user} />;
    if (access === "verified") return <>{children}</>;
    return <AdminLogin />;
};

export default AdminRoute;
